import { useEffect, useState } from 'react';

import { getLogger } from '../utils/logger/logger';

const HOUR_MS = 60 * 60 * 1000;
const HEARTBEAT_MS = 60 * 1000;

// Lets testers shorten both thresholds without waiting hours
const OVERRIDE_HOURS = (() => {
  try {
    const override = parseFloat(localStorage.getItem('max_inactive_hours') || '-1');
    if (Number.isFinite(override) && override >= 0 && override < 12) {
      return override;
    }
  } catch {
    /* */
  }
  return undefined;
})();

const MAX_AWAY_MS = (OVERRIDE_HOURS ?? 2) * HOUR_MS;
// After a sleep this long the stream is broken regardless, so a player that still reports playing is not trusted
const MAX_ASLEEP_WHILE_PLAYING_MS = (OVERRIDE_HOURS ?? 8) * HOUR_MS;

const logger = getLogger('[inactivityMonitor]');

const toHours = (ms: number) => Math.round((ms / HOUR_MS) * 10) / 10;

const isVideoPlaying = () =>
  Array.from(document.querySelectorAll('video')).some(video => !video.paused && !video.ended);

// The slice of the Chromecast sender SDK read here; its typings live in play-player
interface CastSdk {
  framework?: {
    CastContext: {
      getInstance: () => {
        getCurrentSession: () => { getSessionState: () => string } | null;
      };
    };
  };
}

// Mirrors isCastConnected in play-player: a connected session counts even when paused, since a reload drops it
const isCasting = () => {
  try {
    const castSdk = (globalThis as { cast?: CastSdk }).cast;
    const sessionState = castSdk?.framework?.CastContext.getInstance().getCurrentSession()?.getSessionState();
    return sessionState === 'SESSION_STARTED' || sessionState === 'SESSION_RESUMED';
  } catch {
    return false;
  }
};

export const isPlaybackActive = () => isVideoPlaying() || isCasting();

export interface LongInactivity {
  reason: 'asleep' | 'away';
  hoursAway: number;
  /** Whether video or a cast session reported playing when the user came back */
  wasPlaying: boolean;
}

/**
 * @description Reports, once, that the user is back after being away long enough that the app should be reloaded.
 * Browsers keep timers running in a background tab that plays media, so a long gap between heartbeats means the
 * machine slept (or the tab was frozen), never that someone is still watching.
 */
export const useInactivityMonitor = (enabled = true) => {
  const [longInactivity, setLongInactivity] = useState<LongInactivity>();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    logger.info({
      maxAwayHours: toHours(MAX_AWAY_MS),
      maxAsleepWhilePlayingHours: toHours(MAX_ASLEEP_WHILE_PLAYING_MS),
    });

    let lastHeartbeat = Date.now();
    let hiddenSince = Date.now();
    let longestGapWhileHidden = 0;

    const onReturn = (awayMs: number, asleepMs: number) => {
      const wasPlaying = isPlaybackActive();
      if (asleepMs >= MAX_ASLEEP_WHILE_PLAYING_MS) {
        logger.info(`back after ${toHours(asleepMs)}h asleep`);
        setLongInactivity({ reason: 'asleep', hoursAway: toHours(asleepMs), wasPlaying });
      } else if (awayMs >= MAX_AWAY_MS && !wasPlaying) {
        logger.info(`back after ${toHours(awayMs)}h away`);
        setLongInactivity({ reason: 'away', hoursAway: toHours(awayMs), wasPlaying });
      }
    };

    const onHeartbeat = () => {
      const now = Date.now();
      const gap = now - lastHeartbeat;
      lastHeartbeat = now;
      if (document.hidden) {
        longestGapWhileHidden = Math.max(longestGapWhileHidden, gap);
      } else if (gap >= MAX_AWAY_MS) {
        onReturn(gap, gap);
      }
    };

    const onVisibilityChange = () => {
      const now = Date.now();
      if (document.hidden) {
        hiddenSince = now;
        longestGapWhileHidden = 0;
        return;
      }
      const asleepMs = Math.max(longestGapWhileHidden, now - lastHeartbeat);
      lastHeartbeat = now;
      onReturn(now - hiddenSince, asleepMs);
    };

    const interval = setInterval(onHeartbeat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);

  return { longInactivity };
};
